import { Switch, Route, Redirect, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth, hasStytchSessionCookie } from "@/hooks/useAuth";
import { pathNeedsStytch } from "@/utils/stytchRoutes";
import { Suspense, useEffect, useState } from "react";
import LoadingSpinner from "@/components/LoadingSpinner";
import { lazyImports, preloadCriticalRoutes, createLazyImport, createPreloadableLazyImport } from "@/utils/lazyImports";
import { initializePerformanceOptimizations } from "@/utils/performanceOptimization";
import { RoleGuard, CandidateOnly, CompanyOnly, CompanyAuthOnly, AdminOnly } from "@/components/RoleGuard";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { logger } from "@/utils/logger";
import { useCVPolling } from "@/hooks/useCVPolling";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { useGoogleAnalytics } from "@/hooks/useGoogleAnalytics";
import { CandidateLayout } from "@/components/layouts/CandidateLayout";
import { CompanyLayout } from "@/components/layouts/CompanyLayout";
import { getSubdomain, isAppSite, getAppUrl, getMarketingUrl } from "@/utils/subdomain";
import { saveReturnUrl } from "@/utils/stytchAuthHelpers";

// Immediate load: only what's needed to render the homepage without any waterfall
import NotFound from "@/pages/not-found";
import CompanyHomePage from "@/pages/website/CompanyHomePage";
import FeaturePage from "@/pages/website/FeaturePage";

// Eager: GDPR consent page is small and reached from email links — no benefit
// from chunking, and we want the first paint to be instant for compliance UX.
import GdprConsentPage from "@/pages/gdpr-consent";

// Prerendered public pages whose components are code-split. They come from the
// shared preloadable registry so main.tsx can resolve the route's chunk BEFORE
// React's first render — otherwise the prerendered copy is replaced by the
// route-level Suspense spinner while the chunk downloads (the "content flash").
import {
  CandidateHomePage,
  AboutPage,
  ContactPage,
  PricingPage,
  HiringAppPricingPage,
  AtsPricingPage,
  CvVsMarketPage,
  SampleScorecardPage,
  ConnectYourAtsPage,
  WhatYouGetPage,
  HowItWorks,
  TermsAndConditions,
  PrivacyPolicy,
  CookiePolicy,
  SecurityPage,
  PublicJobs,
  PublicJobDetail,
} from "@/utils/publicPages";

// Lazy provider boundary — loads the Stytch auth SDK only when a route needs it.
// Mounting it lazily keeps the SDK off the critical path for public marketing pages.
// Preloadable so main.tsx can resolve the chunk BEFORE the first render when a
// session cookie is present: a logged-in visitor on a prerendered marketing page
// otherwise watches the copy get replaced by the gate's Suspense spinner while
// the SDK chunk downloads.
const StytchProviders = createPreloadableLazyImport(() => import("@/components/StytchProviders"));
export const preloadStytchProviders = StytchProviders.preload;

// Auth pages are lazy-loaded so the Stytch SDK they import is NOT pulled into the
// main entry chunk. The SDK module is still a single shared instance: Vite emits one
// shared vendor chunk for @stytch/* imported by these pages and by StytchProviders.
const CandidateStytchAuth = createLazyImport(() => import("@/pages/candidate-stytch-auth"));
const CompanyStytchAuth = createLazyImport(() => import("@/pages/company-stytch-auth"));

// Lazy-loaded pages — split into separate chunks, downloaded only when navigated to
const Landing = createLazyImport(() => import("@/pages/landing"));
const MyApplications = createLazyImport(() => import("@/pages/my-applications"));
const AgentMarketplace = createLazyImport(() => import("@/pages/agent-marketplace"));
const AgentChat = createLazyImport(() => import("@/pages/agent-chat"));
const JobGroupedApplications = createLazyImport(() =>
  import("@/pages/JobGroupedApplications").then(m => ({ default: m.JobGroupedApplications }))
);
const CompanyProfile = createLazyImport(() => import("@/pages/company-profile"));
const CalendarSettings = createLazyImport(() => import("@/pages/CalendarSettings"));
const CompanySettings = createLazyImport(() => import("@/pages/CompanySettings"));
const InterviewScheduling = createLazyImport(() => import("@/pages/interview-scheduling"));
const AdminDashboard = createLazyImport(() => import("@/pages/admin-dashboard"));
const AdminLogin = createLazyImport(() => import("@/pages/admin-login"));
const CvVsMarketResultPage = createLazyImport(() => import("@/pages/website/CvVsMarketResultPage"));
const CandidateCallback = createLazyImport(() => import("@/pages/candidate-callback"));
const CandidateResetPassword = createLazyImport(() => import("@/pages/candidate-reset-password"));
const CompanyCallback = createLazyImport(() => import("@/pages/company-callback"));
const CompanyResetPassword = createLazyImport(() => import("@/pages/company-reset-password"));
const PublicInterviewScheduling = createLazyImport(() => import("@/pages/public-interview-scheduling"));
const AuthStatusRedirect = createLazyImport(() => import("@/pages/auth-status-redirect"));

// Destructure lazy imports for cleaner code
const {
  auth: { CompanyAuth, AuthLogin, AuthSignup, AuthRecover, AuthConfirm },
  dashboard: { CandidateDashboard, CompanyDashboard },
  jobManagement: { JobPosting, JobsOverview, JobEdit, JobView },
  jobSearch: { JobSearch, JobDetail },
  aiMatching: { MatchingAnalysis, CandidateMatching, AIMatchingTest },
  profile: { CandidateProfile, MockInterview },
  setup: { CompanySetup },
} = lazyImports;

// Optimized JobRecommendations component
const JobRecommendations = createLazyImport(() => import("@/components/OptimizedJobRecommendations"));

// Component that handles global CV polling
function CVPollingHandler() {
  useCVPolling();
  return null;
}

// Persistent layout wrapper for candidate routes - SidebarProvider stays mounted across navigation
function CandidateRoutes({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  return (
    <CandidateLayout activeRoute={location}>
      {children}
    </CandidateLayout>
  );
}

// Persistent layout wrapper for company routes - SidebarProvider stays mounted across navigation
function CompanyRoutes({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  return (
    <CompanyLayout activeRoute={location}>
      {children}
    </CompanyLayout>
  );
}

// Helper component to render role-based dashboard for authenticated users
// Used to redirect authenticated users from marketing pages to their appropriate dashboard
function RoleBasedDashboard({ 
  user, 
  fallback 
}: { 
  user: { role?: string } | null | undefined; 
  fallback: React.ReactNode;
}) {
  if (user?.role === 'admin') {
    return <AdminDashboard />;
  } else if (user?.role === 'candidate') {
    return <CandidateRoutes><CandidateDashboard /></CandidateRoutes>;
  } else if (user?.role === 'company') {
    return <CompanyRoutes><CompanyDashboard /></CompanyRoutes>;
  }
  return <>{fallback}</>;
}

function Router() {
  const { isAuthenticated, isLoading, user, authReady } = useAuth();
  const [location] = useLocation();

  // Initialize cookie consent on all pages
  // Cookie preferences apply to all users regardless of authentication status
  useCookieConsent(true);
  
  // Initialize Google Analytics on public pages only
  useGoogleAnalytics();

  // Check if current route is public (doesn't require authentication)
  // Public routes: /jobs (with query params), /jobs/:slug, /legal/*, / (landing), /auth/*, /candidate-auth, /company-auth
  // Protected routes to exclude: /jobs-overview (company route)
  const protectedJobRoutes = ['/jobs-overview'];
  const isJobsRoute = location === '/jobs' || 
                      location.startsWith('/jobs/') || 
                      location.startsWith('/jobs?') || 
                      location.startsWith('/jobs#');
  const isProtectedJobRoute = protectedJobRoutes.some(route => location.startsWith(route));
  
  const isPublicRoute = location === '/' || 
                        location === '/candidates' ||
                        location.startsWith('/company/about') ||
                        location.startsWith('/company/contact') ||
                        location.startsWith('/company/pricing') ||
                        (isJobsRoute && !isProtectedJobRoute) ||
                        location.startsWith('/legal/') ||
                        location.startsWith('/auth/') ||
                        location.startsWith('/candidate-auth') ||
                        location.startsWith('/company-auth') ||
                        location.startsWith('/schedule/') ||
                        location.startsWith('/cv-vs-market') ||
                        location.startsWith('/sample-scorecard') ||
                        location.startsWith('/connect-your-ats') ||
                        location === '/what-you-get' ||
                        location === '/how-it-works' ||
                        // The 12 feature landing pages. Missing here, they were
                        // treated as protected: an anonymous visitor got the
                        // "Authenticating..." spinner over the prerendered copy
                        // until the auth query settled.
                        location.startsWith('/features/');

  // Save return URL for protected routes BEFORE any redirects happen
  // This ensures deep links work regardless of subdomain
  useEffect(() => {
    if (authReady && !isAuthenticated && !isPublicRoute) {
      const currentPath = window.location.pathname + window.location.search;
      saveReturnUrl(currentPath);
      console.log('[App] Saved return URL for protected route:', currentPath);
    }
  }, [authReady, isAuthenticated, isPublicRoute]);

  // Redirect unauthenticated users from app subdomain to marketing site
  // This ensures app.talenture.ai is exclusively for authenticated users
  // EXCEPT for auth callback routes which need to complete before redirecting
  const isAuthRoute = location.startsWith('/auth/') || 
                      location.startsWith('/candidate-auth') || 
                      location.startsWith('/company-auth');
  
  useEffect(() => {
    if (authReady && !isAuthenticated && isAppSite() && !isAuthRoute) {
      const marketingUrl = getMarketingUrl('/');
      if (marketingUrl.startsWith('http')) {
        window.location.href = marketingUrl;
      }
    }
  }, [authReady, isAuthenticated, isAuthRoute]);

  // CRITICAL: Prevent route evaluation until auth state is definitively known
  // Wait for authReady (status === 'success' || 'error') to prevent NotFound flash
  // This blocks routing during the transient 401 response that occurs before
  // session cookies are honored by the backend.
  // Public routes (marketing pages, legal, auth) render immediately — they don't
  // depend on auth state and have no risk of a NotFound flash, so blocking them
  // causes an unnecessary blank-screen → page flicker on every cold visit.
  if (!authReady && !isPublicRoute) {
    return <LoadingSpinner message="Authenticating..." />;
  }

  return (
    <ErrorBoundary context="Main Application Router">
      <Suspense fallback={<LoadingSpinner message="Loading page..." />}>
        <Switch>
          {/* Auth transition route - accessible in both authenticated and unauthenticated states */}
          <Route path="/auth/status" component={AuthStatusRedirect} />
          
          {/* Public interview scheduling - token-based access, no auth required */}
          <Route path="/schedule/:token" component={({ params }) => (
            <PublicInterviewScheduling token={params.token || ''} />
          )} />

          {/* GDPR re-consent confirmation (Task #176) — public, token-based, no auth */}
          <Route path="/gdpr/consent" component={GdprConsentPage} />
          
          {isAuthenticated ? (
          <>
            <Route path="/" component={() => {
              if (user?.role === 'admin') {
                return <AdminDashboard />;
              } else if (user?.role === 'candidate') {
                return <CandidateRoutes><CandidateDashboard /></CandidateRoutes>;
              } else if (user?.role === 'company') {
                return <CompanyRoutes><CompanyDashboard /></CompanyRoutes>;
              }
              return <Landing />;
            }} />
            
            {/* Stytch auth routes - redirect to dashboard if authenticated */}
            <Route path="/candidate-auth" component={() => {
              if (user?.role === 'candidate') {
                return <CandidateRoutes><CandidateDashboard /></CandidateRoutes>;
              } else if (user?.role === 'company') {
                return <CompanyRoutes><CompanyDashboard /></CompanyRoutes>;
              } else if (user?.role === 'admin') {
                return <AdminDashboard />;
              }
              return <Landing />;
            }} />
            <Route path="/company-auth" component={() => {
              if (user?.role === 'candidate') {
                return <CandidateRoutes><CandidateDashboard /></CandidateRoutes>;
              } else if (user?.role === 'company') {
                return <CompanyRoutes><CompanyDashboard /></CompanyRoutes>;
              } else if (user?.role === 'admin') {
                return <AdminDashboard />;
              }
              return <Landing />;
            }} />
            
            {/* Stytch callback routes - handle authentication tokens */}
            <Route path="/auth/candidate/callback" component={CandidateCallback} />
            <Route path="/auth/candidate/reset-password" component={CandidateResetPassword} />
            <Route path="/auth/company/callback" component={CompanyCallback} />
            <Route path="/auth/company/reset-password" component={CompanyResetPassword} />
            
            <Route path="/dashboard" component={() => {
              if (user?.role === 'candidate') {
                return <CandidateRoutes><CandidateDashboard /></CandidateRoutes>;
              } else if (user?.role === 'company') {
                return <CompanyRoutes><CompanyDashboard /></CompanyRoutes>;
              }
              return <Landing />;
            }} />
            {/* Candidate-only routes - wrapped in CandidateRoutes for persistent sidebar */}
            <Route path="/candidate-dashboard" component={() => (
              <CandidateOnly><CandidateRoutes><CandidateDashboard /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/job-search" component={() => (
              <CandidateOnly><CandidateRoutes><JobSearch /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/my-applications" component={() => (
              <CandidateOnly><CandidateRoutes><MyApplications /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/mock-interview/:jobId/:jobTitle" component={({ params }) => (
              <CandidateOnly>
                <CandidateRoutes>
                  <MockInterview jobId={params.jobId} jobTitle={decodeURIComponent(params.jobTitle)} />
                </CandidateRoutes>
              </CandidateOnly>
            )} />
            <Route path="/job-recommendations" component={() => (
              <CandidateOnly><CandidateRoutes><JobRecommendations /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/interview-scheduling/:jobId" component={({ params }) => (
              <CandidateOnly><CandidateRoutes><InterviewScheduling jobId={params?.jobId || ''} /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/candidate-profile" component={() => (
              <CandidateOnly><CandidateRoutes><CandidateProfile /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/agents" component={() => (
              <CandidateOnly><CandidateRoutes><AgentMarketplace /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/agents/:agentId/chat/:conversationId" component={({ params }) => (
              <CandidateOnly><CandidateRoutes><AgentChat agentId={params.agentId} conversationId={params.conversationId} /></CandidateRoutes></CandidateOnly>
            )} />
            <Route path="/job/:jobId" component={({ params }) => (
              <CandidateOnly><CandidateRoutes><JobDetail /></CandidateRoutes></CandidateOnly>
            )} />
            
            {/* /job-detail/:id route for authenticated candidates (same as /job/:jobId but different path) */}
            <Route path="/job-detail/:id" component={({ params }) => (
              <CandidateOnly><CandidateRoutes><JobDetail jobId={params?.id} /></CandidateRoutes></CandidateOnly>
            )} />

            {/* Company-only routes - wrapped in CompanyRoutes for persistent sidebar */}
            {/* Company setup - requires company auth but not company profile */}
            <Route path="/company-setup" component={() => (
              <CompanyAuthOnly><CompanySetup /></CompanyAuthOnly>
            )} />
            <Route path="/company/setup" component={() => (
              <CompanyAuthOnly><CompanySetup /></CompanyAuthOnly>
            )} />
            
            <Route path="/company-dashboard" component={() => (
              <CompanyOnly><CompanyRoutes><CompanyDashboard /></CompanyRoutes></CompanyOnly>
            )} />
            <Route path="/job-posting" component={() => (
              <CompanyOnly><CompanyRoutes><JobPosting /></CompanyRoutes></CompanyOnly>
            )} />
            <Route path="/jobs-overview" component={() => (
              <CompanyOnly><CompanyRoutes><JobsOverview /></CompanyRoutes></CompanyOnly>
            )} />
            <Route path="/job-posting/:id/edit" component={({ params }) => (
              <CompanyOnly><CompanyRoutes><JobEdit /></CompanyRoutes></CompanyOnly>
            )} />
            <Route path="/job-view/:id" component={({ params }) => (
              <CompanyOnly><CompanyRoutes><JobView /></CompanyRoutes></CompanyOnly>
            )} />
            {/* Redirect legacy /incoming-applications to /applications-board */}
            <Route path="/incoming-applications" component={() => {
              window.location.replace('/applications-board');
              return null;
            }} />
            <Route path="/applications-board" component={() => (
              <CompanyOnly><CompanyRoutes><JobGroupedApplications /></CompanyRoutes></CompanyOnly>
            )} />
            <Route path="/company-profile" component={() => (
              <CompanyOnly><CompanyRoutes><CompanyProfile /></CompanyRoutes></CompanyOnly>
            )} />
            <Route path="/matching/analysis/:candidateId/:jobId" component={({ params }) => (
              <CompanyOnly>
                <CompanyRoutes>
                  <MatchingAnalysis candidateId={params.candidateId} jobId={params.jobId} />
                </CompanyRoutes>
              </CompanyOnly>
            )} />
            <Route path="/job/:jobId/candidates" component={({ params }) => (
              <CompanyOnly>
                <CompanyRoutes>
                  <CandidateMatching jobId={params.jobId} jobTitle="Job" />
                </CompanyRoutes>
              </CompanyOnly>
            )} />
            <Route path="/calendar-settings" component={() => (
              <CompanyOnly><CompanyRoutes><CalendarSettings /></CompanyRoutes></CompanyOnly>
            )} />
            <Route path="/settings" component={() => (
              <CompanyOnly><CompanyRoutes><CompanySettings /></CompanyRoutes></CompanyOnly>
            )} />

            {/* Admin routes - non-obvious URLs for security */}
            <Route path="/system/login" component={AdminLogin} />
            <Route path="/system/dashboard" component={() => (
              <AdminOnly><AdminDashboard /></AdminOnly>
            )} />

            {/* Development/Testing routes - require authentication but any role */}
            <Route path="/ai-matching-test" component={() => (
              <RoleGuard allowedRoles={['candidate', 'company']}>
                <AIMatchingTest />
              </RoleGuard>
            )} />
            <Route path="/test-status" component={() => {
              const TestStatus = createLazyImport(() => import("@/pages/test-status"));
              return (
                <CompanyOnly>
                  <CompanyRoutes>
                    <Suspense fallback={<LoadingSpinner message="Loading test page..." />}>
                      <TestStatus />
                    </Suspense>
                  </CompanyRoutes>
                </CompanyOnly>
              );
            }} />

            {/* Job search and public job detail - accessible when authenticated */}
            <Route path="/jobs/:slug" component={({ params }) => (
              <PublicJobDetail />
            )} />
            <Route path="/jobs" component={PublicJobs} />
            
            {/* Public website pages - redirect authenticated users to their dashboards */}
            <Route path="/candidates" component={() => (
              <RoleBasedDashboard user={user} fallback={<CandidateHomePage />} />
            )} />
            <Route path="/company/about" component={() => (
              <RoleBasedDashboard user={user} fallback={<AboutPage />} />
            )} />
            <Route path="/company/contact" component={() => (
              <RoleBasedDashboard user={user} fallback={<ContactPage />} />
            )} />
            <Route path="/pricing"><Redirect to="/company/pricing" replace /></Route>
            <Route path="/company/pricing" component={() => (
              <RoleBasedDashboard user={user} fallback={<PricingPage />} />
            )} />
            <Route path="/company/pricing/hiring-app" component={() => (
              <RoleBasedDashboard user={user} fallback={<HiringAppPricingPage />} />
            )} />
            <Route path="/company/pricing/ats-layer" component={() => (
              <RoleBasedDashboard user={user} fallback={<AtsPricingPage />} />
            )} />
            
            {/* Public viral upload + result pages — available to authenticated users too. */}
            <Route path="/cv-vs-market" component={CvVsMarketPage} />
            <Route path="/sample-scorecard" component={SampleScorecardPage} />
            <Route path="/cv-vs-market/result/:token" component={CvVsMarketResultPage} />
            <Route path="/cv-vs-market/r/:shareId" component={CvVsMarketResultPage} />

            {/* ATS integration marketing page */}
            <Route path="/connect-your-ats" component={ConnectYourAtsPage} />

            {/* What you get — the per-candidate candidate scorecard */}
            <Route path="/what-you-get" component={WhatYouGetPage} />

            {/* How it works — the hiring mission overview */}
            <Route path="/how-it-works" component={HowItWorks} />

            {/* Feature landing pages */}
            <Route path="/features/:slug" component={FeaturePage} />

            {/* Legal pages - accessible to all users */}
            <Route path="/legal/terms" component={TermsAndConditions} />
            <Route path="/legal/privacy" component={PrivacyPolicy} />
            <Route path="/legal/cookies" component={CookiePolicy} />
            <Route path="/legal/security" component={SecurityPage} />
          </>
        ) : (
          <>
            {/* Public routes - only when NOT authenticated */}
            <Route path="/jobs/:slug" component={({ params }) => (
              <PublicJobDetail />
            )} />
            <Route path="/jobs" component={PublicJobs} />
            
            {/* Public website homepage */}
            <Route path="/" component={CompanyHomePage} />
            <Route path="/candidates" component={CandidateHomePage} />
            <Route path="/company/about" component={AboutPage} />
            <Route path="/company/contact" component={ContactPage} />
            <Route path="/pricing"><Redirect to="/company/pricing" replace /></Route>
            <Route path="/company/pricing" component={PricingPage} />
            <Route path="/company/pricing/hiring-app" component={HiringAppPricingPage} />
            <Route path="/company/pricing/ats-layer" component={AtsPricingPage} />
            
            {/* Legacy auth routes */}
            <Route path="/auth/login" component={AuthLogin} />
            <Route path="/auth/signup" component={AuthSignup} />
            <Route path="/auth/recover" component={AuthRecover} />
            <Route path="/auth/confirm" component={AuthConfirm} />
            <Route path="/auth/company" component={CompanyAuth} />
            
            {/* Stytch authentication routes */}
            <Route path="/candidate-auth" component={CandidateStytchAuth} />
            <Route path="/company-auth" component={CompanyStytchAuth} />
            
            {/* Stytch callback routes - handle authentication tokens */}
            <Route path="/auth/candidate/callback" component={CandidateCallback} />
            <Route path="/auth/candidate/reset-password" component={CandidateResetPassword} />
            <Route path="/auth/company/callback" component={CompanyCallback} />
            <Route path="/auth/company/reset-password" component={CompanyResetPassword} />
            
            {/* Admin login - accessible when not authenticated */}
            <Route path="/system/login" component={AdminLogin} />
            <Route path="/system/dashboard" component={() => <AdminLogin />} />
            
            {/* Public viral upload + result pages */}
            <Route path="/cv-vs-market" component={CvVsMarketPage} />
            <Route path="/sample-scorecard" component={SampleScorecardPage} />
            <Route path="/cv-vs-market/result/:token" component={CvVsMarketResultPage} />
            <Route path="/cv-vs-market/r/:shareId" component={CvVsMarketResultPage} />

            {/* ATS integration marketing page */}
            <Route path="/connect-your-ats" component={ConnectYourAtsPage} />

            {/* What you get — the per-candidate candidate scorecard */}
            <Route path="/what-you-get" component={WhatYouGetPage} />

            {/* How it works — the hiring mission overview */}
            <Route path="/how-it-works" component={HowItWorks} />

            {/* Feature landing pages */}
            <Route path="/features/:slug" component={FeaturePage} />

            {/* Legal pages - accessible to all users */}
            <Route path="/legal/terms" component={TermsAndConditions} />
            <Route path="/legal/privacy" component={PrivacyPolicy} />
            <Route path="/legal/cookies" component={CookiePolicy} />
            <Route path="/legal/security" component={SecurityPage} />
          </>
        )}
        
        <Route component={NotFound} />
      </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

/**
 * Reactive boundary that mounts the Stytch providers (and loads the Stytch SDK
 * chunk) only when the current route — or the presence of a Stytch session cookie
 * for an authenticated visitor — requires it.
 *
 * It is "sticky": once activated it stays mounted for the rest of the page session.
 * This keeps the module-level Stytch clients (in StytchProviders) alive across
 * navigation and avoids any provider teardown/re-init churn once auth is in play.
 *
 * Enablement is computed SYNCHRONOUSLY during render (not only in an effect) so the
 * providers mount in the same render the route changes to an auth/app route. If we
 * relied on an effect to flip a flag, there would be one render where an
 * already-resolved lazy auth page renders without a provider and its useStytch()
 * call would throw. The effect only latches the sticky bit for subsequent renders.
 */
// Warm the lazy Stytch provider chunk (and the @stytch/* vendor chunk it pulls in).
// Uses the same import specifier as the `StytchProviders` lazy() above, so the module
// graph is deduplicated and a subsequent lazy mount resolves synchronously from cache.
function prefetchStytchProviders(): void {
  void import("@/components/StytchProviders").catch(() => {
    // Best-effort prefetch — ignore network/offline failures; the real lazy mount retries.
  });
}

// Schedule the prefetch for when the browser is idle (falling back to a short timer
// where requestIdleCallback is unavailable, e.g. Safari). Returns a cleanup function so
// a pending prefetch is cancelled if the gate enables or unmounts first.
function prefetchStytchProvidersWhenIdle(): () => void {
  const w = window as Window & {
    requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    cancelIdleCallback?: (handle: number) => void;
  };

  if (typeof w.requestIdleCallback === "function") {
    const handle = w.requestIdleCallback(prefetchStytchProviders, { timeout: 2000 });
    return () => w.cancelIdleCallback?.(handle);
  }

  const timer = window.setTimeout(prefetchStytchProviders, 1200);
  return () => window.clearTimeout(timer);
}

function StytchProviderGate({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const [latched, setLatched] = useState(
    () => pathNeedsStytch(location) || hasStytchSessionCookie()
  );

  const shouldEnable =
    latched || pathNeedsStytch(location) || hasStytchSessionCookie();

  useEffect(() => {
    if (shouldEnable && !latched) {
      setLatched(true);
    }
  }, [shouldEnable, latched]);

  // Background pre-warm: on routes that do NOT yet need Stytch (marketing/content),
  // fetch the Stytch provider chunk during browser idle time. This keeps the SDK off
  // the critical path for first paint (the Lighthouse win) while ensuring that a later
  // navigation to a login/auth route resolves the ~496KB chunk from cache instead of a
  // cold download — so the login redirect feels instant. Best-effort: failures (offline,
  // etc.) are ignored, and it never runs once the gate is already enabled.
  useEffect(() => {
    if (shouldEnable) return;
    return prefetchStytchProvidersWhenIdle();
  }, [shouldEnable]);

  if (!shouldEnable) {
    return <>{children}</>;
  }

  return (
    <Suspense fallback={<LoadingSpinner message="Loading..." />}>
      <StytchProviders>{children}</StytchProviders>
    </Suspense>
  );
}

function App() {
  useEffect(() => {
    initializePerformanceOptimizations();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <CVPollingHandler />
        <Toaster />
        <StytchProviderGate>
          <Router />
        </StytchProviderGate>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
